import { useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { RegisterEntry } from "@/api/types";
import { formatQuantity } from "@/lib/registerQuantity";
import { RegisterQuantityInputs } from "./RegisterQuantityInputs";
import "./RegisterQuantityField.css";

export interface RegisterQuantityFieldProps {
  entry: RegisterEntry;
  /** Re-reads the register after every write; the server's answer is the truth. */
  refresh: () => Promise<void>;
}

export function RegisterQuantityField({ entry, refresh }: RegisterQuantityFieldProps) {
  const [editing, setEditing] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const state = !entry.quantityEditable ? "split" : editing ? "editing" : entry.quantity === null ? "empty" : "set";

  function startEditing(): void {
    setQuantity(entry.quantity === null ? "" : String(entry.quantity));
    setUnit(entry.unit ?? "");
    setError(null);
    setEditing(true);
  }

  async function save(clear = false): Promise<void> {
    const value = Number(quantity);
    if (!clear && (!quantity.trim() || !Number.isFinite(value) || value <= 0 || !unit)) {
      setError("Enter a quantity greater than zero and choose a unit.");
      return;
    }
    setPending(true);
    try {
      await demoApi.setRegisterQuantity(entry.entryLineId, {
        quantity: clear ? null : value, unit: clear ? null : unit,
      });
      await refresh();
      setError(null);
      setEditing(false);
    } catch (err) {
      setError(problemMessage(err, "Could not save this quantity."));
    } finally {
      setPending(false);
    }
  }

  return (
    <div role="group" aria-label="Quantity" className="register-quantity" data-state={state}>
      {state === "split" ? <span>Quantities are per line.</span> : state === "editing" ? (
        <>
          <RegisterQuantityInputs quantity={quantity} unit={unit} onQuantityChange={setQuantity}
            onUnitChange={setUnit} disabled={pending} />
          <div className="register-quantity__actions">
            <button type="button" className="btn btn--ghost" disabled={pending} onClick={() => void save()}>Save quantity</button>
            <button type="button" className="btn btn--ghost" disabled={pending}
              onClick={() => { setEditing(false); setError(null); }}>Cancel</button>
            {entry.quantity !== null && <button type="button" className="btn btn--ghost" disabled={pending}
              onClick={() => void save(true)}>Clear quantity</button>}
          </div>
          {error && <span role="alert" className="register-quantity__error">{error}</span>}
        </>
      ) : (
        <>
          {entry.quantity !== null && <span>{formatQuantity(entry.quantity, entry.unit ?? "")}</span>}
          <button type="button" className="btn btn--ghost" onClick={startEditing}>
            {state === "empty" ? "Add quantity" : "Edit quantity"}
          </button>
        </>
      )}
    </div>
  );
}
